import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState, HdBodygraphSide, HdPlanetGridVisualizationMode } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdPlanetActivationBlockClickEvent, HdPlanetGridDensity } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { HdPhsBlockClickEvent } from '../hd-color-tone-block/hd-color-tone-block.component';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { getHdPlanetDisplayConfig, getHdPlanetDisplayConfigByPlanetId, getHdPlanetsBySection, HdPlanetKey } from '../hd-planet-display-config';
import { findActivationFromList, resolveBaseHoverTarget } from '../hd-planet-grid.utils';
import { HdIncarnationCrossComponent } from '../hd-incarnation-cross/hd-incarnation-cross.component';
import { HdLifeCrossComponent } from '../hd-life-cross/hd-life-cross.component';
import { HdLunarPlanetarySquareComponent } from '../hd-lunar-planetary-square/hd-lunar-planetary-square.component';
import { HdResonanceLunarPlanetarySquareComponent } from '../hd-resonance-lunar-planetary-square/hd-resonance-lunar-planetary-square.component';
import { HdLineStatsComponent } from '../hd-line-stats/hd-line-stats.component';
import { HdCircuitStatsComponent } from '../hd-circuit-stats/hd-circuit-stats.component';
import { HdChironStatsComponent } from '../hd-chiron-stats/hd-chiron-stats.component';
import { HdSimpleExtraPlanetVm } from '../hd-simple-planet-grid/hd-simple-planet-grid.component';
import { HdChironClickEvent, HdChironPosition } from '../../hd-chiron.models';
import { HdAstroPointClickEvent, HdAstroPointPosition } from '../../hd-astro-points.models';
import { HdAstroPointsComponent } from '../hd-astro-points/hd-astro-points.component';

/**
 * Overkoepelend component dat de drie planeetgroepen samenbrengt:
 * 1. Incarnation Cross (Sun + Earth + Motivation)
 * 2. Life Cross (North Node + South Node + View)
 * 3. Lunar Planetary Square (Moon – Pluto 3×3)
 *
 * De oriëntatie (links-naar-rechts of gespiegeld) wordt bepaald door de side.
 */
@Component({
  selector: 'hd-planet-grid',
  templateUrl: './hd-planet-grid.component.html',
  styleUrl: './hd-planet-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdIncarnationCrossComponent, HdLifeCrossComponent, HdLunarPlanetarySquareComponent, HdResonanceLunarPlanetarySquareComponent, HdLineStatsComponent, HdCircuitStatsComponent, HdChironStatsComponent, HdAstroPointsComponent],
  host: {
    '[class.is-hover-active]': 'hoverActive()',
    '[class.is-stat-frozen]': 'statFrozen()',
    '[class.resonance-mode]': 'resonanceMode()',
    '[class.density-mini]': "density() === 'mini'",
    '[class.side-design]': "side() === 'design'",
    '[class.side-personality]': "side() === 'personality'",
    '(mouseleave)': 'onHostMouseLeave()',
    '(mouseover)': 'onHostMouseOver($event)',
    '(contextmenu)': 'onHostContextMenu($event)',
  },
})
export class HdPlanetGridComponent {
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  /**
   * Rechterklik op een planet-activatie-blok bubbelt naar de host. Hier
   * filteren we via `closest('hd-planet-activation-block')` zodat enkel
   * blok-rechterkliks doorgegeven worden (niet rechterklik op kleur/toon
   * blokken of stats). De `gate` wordt afgelezen via het `data-gate`-attribuut
   * op het host-element zodat het freeze-contextmenu de target weet — zie
   * `docs/human-design/hd-chart-hover-freeze-additief.md` §4.3.
   */
  readonly planetBlockContextMenuRequested = output<{ gate: number; position: { x: number; y: number } }>();

  /**
   * Of er momenteel een hover-highlight actief is — gebruikt door host-class
   * en SCSS om non-activation-block elementen (kleur/toon blokjes,
   * transformatiepijl, line/circuit stats) te dimmen. Zie
   * `docs/human-design/hd-chart-hover-highlight.md`. Met de app-setting
   * "dimmen bij hover" uit, of met "enkel één kant dimmen" bij een hover vanuit
   * de grid, blijft de class weg: de side-stats dimmen dan niet.
   */
  protected readonly hoverActive = computed(() =>
    (this.hoverService?.enabled() ?? false)
    && (this.hoverService?.isActive() ?? false)
    && (this.hoverService?.dimsGrid() ?? true),
  );

  /**
   * Of er een stat-rij bevroren is. Schakelt het dimmen van het héle
   * side-stats-paneel uit: de stats-componenten dimmen dan zelf per rij, zodat
   * de bevroren rij op volle sterkte kan blijven (een kind kan niet onder de
   * opacity van zijn ouder uit).
   */
  protected readonly statFrozen = computed(() => this.hoverService?.frozenStatKey() != null);

  /**
   * Container-level safety net: dekt het scenario waarin de browser het
   * `mouseleave` van een individueel `hd-planet-activation-block` niet
   * doorgeeft (bv. bij snelle cursorbeweging naar een gebied zonder hover-
   * target). De service debouncet 72ms zodat een direct opvolgende enter
   * binnen of buiten de planet-grid geen tussenliggende clear veroorzaakt.
   */
  protected onHostMouseLeave(): void {
    this.hoverService?.clearForContainer(['planet-grid-block', 'planet-grid-furnishing']);
  }

  /**
   * Gedelegeerde `mouseover`-safety-net op de planet-grid host: dekt het
   * scenario waarin de browser het inner `mouseleave` van een activatie-blok
   * of furnishing-element dropt bij snelle cursorbeweging naar een
   * niet-hoverbaar gebied **binnen** de host (paddings, stats-stroken, …).
   * Hover-targets zijn de three sub-component tags hieronder. Match → no-op,
   * anders → soft clear via de service (idempotent, 72ms debounce).
   */
  protected onHostMouseOver(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (target?.closest('hd-planet-activation-block, hd-color-tone-block, hd-transformation-arrow')) return;
    this.hoverService?.softClearForContainer(['planet-grid-block', 'planet-grid-furnishing']);
  }

  protected onHostContextMenu(event: MouseEvent): void {
    if (!this.contextMenuEnabled()) return;
    const target = event.target as Element | null;
    const block = target?.closest('hd-planet-activation-block') as HTMLElement | null;
    if (!block) return;
    const gateAttr = block.getAttribute('data-gate');
    const gate = gateAttr != null ? Number.parseInt(gateAttr, 10) : NaN;
    if (!Number.isFinite(gate)) return;
    event.preventDefault();
    event.stopPropagation();
    // Pauzeer hover-state mutaties vóór de emit: het CDK-overlay van het
    // menu triggert een mouseleave op het activatie-blok die anders de
    // state zou clearen vóór de gebruiker "Freeze" kan kiezen.
    this.hoverService?.suspendForContextMenu();
    this.planetBlockContextMenuRequested.emit({
      gate,
      position: { x: event.clientX, y: event.clientY },
    });
  }

  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly side = input.required<HdBodygraphSide>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  readonly showGeneKeys = input(false);
  readonly showHdKeynotes = input(false);
  /**
   * Vervangt de klassieke Lunar Planetary Square-sectie door de geavanceerde
   * "Resonance"-variant (verticals/laterals/legende). Incarnation Cross en
   * Life Cross blijven ongewijzigd.
   */
  readonly resonanceMode = input(false);
  /** Toon de zij-statistieken (lijn-/circuit-stats + dormant/defined) onder de grid. */
  readonly showSideStats = input(true);
  /**
   * Toon het eigenlijke planeetgrid-paneel. Wanneer uitgeschakeld blijven de
   * zij-statistieken (mits `showSideStats`) wél zichtbaar; enkel het grid zelf
   * verdwijnt (gebruikt door de zijde-toggle zodat de stats-blokjes niet mee
   * verdwijnen). Default `true` houdt alle bestaande gebruikers ongewijzigd.
   */
  readonly showGrid = input(true);
  /** Classic (enkel cijfers) vs extended (PHS-iconen) weergave. */
  readonly visualizationMode = input<HdPlanetGridVisualizationMode>('extended');
  /**
   * Dichtheid van de grid-blokken (mobiel): `mini` rendert compacte
   * twee-regel-blokken (planeetsymbool + pijltjes / poort.lijn) van 44px zodat
   * het design- én personality-grid naast elkaar op een 375px-telefoon passen.
   * Geldt voor de klassieke grid-secties; de Resonance-LPS blijft erbuiten
   * (die is op phone elders al uitgeschakeld). Zie `docs/MOBIEL.md` §9.
   */
  readonly density = input<HdPlanetGridDensity>('normal');
  /** Activations from the opposite side, used to resolve node line numbers for LPS highlighting. */
  readonly oppositeActivations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  /** All planetary activations for line stats calculation (only used on design side) */
  readonly allActivations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  /** Geselecteerde chart-zijde waarvoor de algemene stats worden vergeleken. */
  readonly statsSide = input<'design' | 'personality' | null>(null);
  /** Ongefilterde render-state voor de algemene dormant/defined-aantallen. */
  readonly statsOverallRenderState = input<HdBodygraphRenderState | null>(null);
  /**
   * Wanneer uitgeschakeld (globale debug-setting "Rechterklik op poorten")
   * laten we de native browser-contextmenu door en emitten we geen
   * freeze-menu — gelijk aan de gate/center-listeners in de chart.
   */
  readonly contextMenuEnabled = input(true);
  readonly planetActivationClicked = output<HdPlanetActivationBlockClickEvent>();
  /**
   * Chiron per zijde voor de statistieken (rechtsonder); `null` = niet tonen
   * (geen data of Chiron-setting uit). Geen activatie, wel klikbaar.
   */
  readonly chironDesign = input<HdSimpleExtraPlanetVm | null>(null);
  readonly chironPersonality = input<HdSimpleExtraPlanetVm | null>(null);
  readonly chironClicked = output<HdChironClickEvent>();
  /**
   * Astro posities van deze zijde (AC, DC, MC, IC, Lilith) voor de rij onder het gridpaneel
   * (spec 2026-10-06); leeg = geen rij (setting uit of geen data). Geen activaties.
   */
  readonly astroPoints = input<readonly HdAstroPointPosition[]>([]);
  /** Chiron van deze zijde als eerste cel van de astro-rij; `null` = niet tonen. */
  readonly astroChiron = input<HdChironPosition | null>(null);
  readonly astroPointClicked = output<HdAstroPointClickEvent>();
  /**
   * Klik op een PHS color/tone-blok (motivatie/determinatie/visie/omgeving).
   * Het event is door de cross-childcomponenten verrijkt met de PHS-categorie
   * + kleur/toon zodat de chart-pagina het bijhorende info-panel kan openen.
   */
  readonly phsBlockClicked = output<HdPhsBlockClickEvent>();

  private readonly sunPlanetId = getHdPlanetDisplayConfig('sun').planetId;
  private readonly earthPlanetId = getHdPlanetDisplayConfig('earth').planetId;
  private readonly northNodePlanetId = getHdPlanetDisplayConfig('northNode').planetId;
  private readonly southNodePlanetId = getHdPlanetDisplayConfig('southNode').planetId;
  private readonly lpsPlanetIds = new Set<number>(
    getHdPlanetsBySection('lunarPlanetarySquare').map(config => config.planetId),
  );

  /**
   * In mini-densiteit forceren we de classic-weergave: PHS-iconen (extended/
   * modern/keynotes) passen niet op de 44px-blokken. De gebruikersinstelling
   * blijft ongewijzigd bewaard voor de normale densiteit.
   */
  protected readonly effectiveVisualizationMode = computed<HdPlanetGridVisualizationMode>(() =>
    this.density() === 'mini' ? 'classic' : this.visualizationMode(),
  );

  protected readonly selectedStatsActivations = computed(() => {
    const selectedSide = this.statsSide();
    if (selectedSide === null) return [];
    return this.allActivations().filter(activation => activation.side === selectedSide);
  });

  protected readonly selectedStatsColor = computed(() => {
    const selectedSide = this.statsSide();
    if (selectedSide === null) return null;
    return this.colorConfig().lifeCross[selectedSide];
  });

  protected readonly sideLabel = computed(() =>
    this.side() === 'personality' ? 'PERSONALITY' : 'DESIGN / BODY',
  );

  protected readonly sideLabelClass = computed(() =>
    this.side() === 'personality' ? 'personality-label' : 'design-label',
  );

  /** Line numbers of Sun and Earth for both sides, used for profile resonance. */
  protected readonly profileLines = computed<ReadonlySet<number>>(() => {
    const lines = new Set<number>();
    const side = this.side();
    const oppositeSide: HdBodygraphSide = side === 'design' ? 'personality' : 'design';

    // Current side Sun/Earth
    const sun = findActivationFromList(this.activations(), side, this.sunPlanetId);
    if (sun?.line != null) lines.add(sun.line);
    const earth = findActivationFromList(this.activations(), side, this.earthPlanetId);
    if (earth?.line != null) lines.add(earth.line);

    // Opposite side Sun/Earth
    const oppSun = findActivationFromList(this.oppositeActivations(), oppositeSide, this.sunPlanetId);
    if (oppSun?.line != null) lines.add(oppSun.line);
    const oppEarth = findActivationFromList(this.oppositeActivations(), oppositeSide, this.earthPlanetId);
    if (oppEarth?.line != null) lines.add(oppEarth.line);

    return lines;
  });

  /** Line numbers of nodes from the current side. */
  protected readonly ownNodeLines = computed<ReadonlySet<number>>(() => {
    const lines = new Set<number>();
    const side = this.side();

    const nn = findActivationFromList(this.activations(), side, this.northNodePlanetId);
    const sn = findActivationFromList(this.activations(), side, this.southNodePlanetId);

    if (nn?.line != null) lines.add(nn.line);
    if (sn?.line != null) lines.add(sn.line);

    return lines;
  });

  /**
   * Mapping `line` → opposite-side LPS gates met die line. Wordt aan de
   * life-cross doorgegeven zodat het link-role node-blok (PSN op
   * personality-zijde, DNN op design-zijde) bij hover over het link-icoon
   * de matching LPS-blokken aan de overkant kan mee oplichten. Zie
   * `docs/human-design/hd-chart-hover-highlight.md` (§ Link-node icon hover).
   */
  protected readonly oppositeLpsLineToGates = computed<ReadonlyMap<number, readonly number[]>>(() => {
    const map = new Map<number, number[]>();
    const oppositeSide: HdBodygraphSide = this.side() === 'design' ? 'personality' : 'design';
    for (const activation of this.oppositeActivations()) {
      if (activation.side !== oppositeSide) continue;
      if (!this.lpsPlanetIds.has(activation.planetId)) continue;
      const bucket = map.get(activation.line);
      if (bucket) {
        if (!bucket.includes(activation.gate)) bucket.push(activation.gate);
      } else {
        map.set(activation.line, [activation.gate]);
      }
    }
    return map;
  });

  /**
   * Map `planetKey` → gate, for the LPS planets of this side and the
   * opposite side. Used by {@link baseHoverTargetGates} to resolve the
   * "corresponding LPS block" of a base badge regardless of whether the
   * mat (and thus the highlight target) sits on the same side or the
   * opposite side (link-node crossover). One map per side keeps the
   * lookup explicit and avoids leaking the cross-side activations any
   * deeper than necessary.
   */
  private readonly sameSideLpsGatesByKey = computed<ReadonlyMap<HdPlanetKey, number>>(() =>
    this.collectLpsGatesByKey(this.activations(), this.side()),
  );

  private readonly oppositeSideLpsGatesByKey = computed<ReadonlyMap<HdPlanetKey, number>>(() => {
    const oppositeSide: HdBodygraphSide = this.side() === 'design' ? 'personality' : 'design';
    return this.collectLpsGatesByKey(this.oppositeActivations(), oppositeSide);
  });

  /**
   * For each base-having planet of this side (Sun, Earth, North Node, South
   * Node), the gate of the corresponding LPS block whose highlight should
   * be triggered when hovering the base badge. Keyed by source planet id
   * so the cross sub-components can lookup their slot in one step.
   *
   * The mapping itself (base + mat → LPS planet, mat → highlight side) is
   * encapsulated in `resolveBaseHoverTarget` — see
   * `.\docs\human-design\base-logic.md` for the spec.
   */
  protected readonly baseHoverTargetGates = computed<ReadonlyMap<number, number>>(() => {
    const result = new Map<number, number>();
    const side = this.side();
    const sameSide = this.sameSideLpsGatesByKey();
    const oppositeSide = this.oppositeSideLpsGatesByKey();

    const sources: { planetId: number; key: HdPlanetKey }[] = [
      { planetId: this.sunPlanetId, key: 'sun' },
      { planetId: this.earthPlanetId, key: 'earth' },
      { planetId: this.northNodePlanetId, key: 'northNode' },
      { planetId: this.southNodePlanetId, key: 'southNode' },
    ];

    for (const { planetId, key } of sources) {
      const activation = findActivationFromList(this.activations(), side, planetId);
      if (!activation) continue;
      const target = resolveBaseHoverTarget(activation.base, key, side);
      if (target == null) continue;
      const gates = target.side === side ? sameSide : oppositeSide;
      const targetGate = gates.get(target.planetKey);
      if (targetGate == null) continue;
      result.set(planetId, targetGate);
    }
    return result;
  });

  private collectLpsGatesByKey(
    activations: readonly HdBodygraphPlanetaryActivation[],
    side: HdBodygraphSide,
  ): ReadonlyMap<HdPlanetKey, number> {
    const map = new Map<HdPlanetKey, number>();
    for (const activation of activations) {
      if (activation.side !== side) continue;
      if (!this.lpsPlanetIds.has(activation.planetId)) continue;
      const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
      if (!config) continue;
      map.set(config.key, activation.gate);
    }
    return map;
  }

  /** Collects the line numbers of the opposite side's node activations for LPS line matching. */
  protected readonly oppositeNodeLines = computed<ReadonlySet<number>>(() => {
    const activations = this.oppositeActivations();
    const oppositeSide: HdBodygraphSide = this.side() === 'design' ? 'personality' : 'design';
    const lines = new Set<number>();

    const nn = findActivationFromList(activations, oppositeSide, this.northNodePlanetId);
    const sn = findActivationFromList(activations, oppositeSide, this.southNodePlanetId);

    if (nn?.line != null) lines.add(nn.line);
    if (sn?.line != null) lines.add(sn.line);

    return lines;
  });
}
